import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  imageWidth = 1024,
  imageHeight = 1024,
  type = 'website',
  structuredData,
  geoRegion,
  geoPlacename
}) => {
  const location = useLocation();
  const siteUrl = 'https://www.ateverydesk.com';
  const fullUrl = `${siteUrl}${location.pathname}`;
  const defaultImage = `${siteUrl}/assets/Logo.png`;
  const region = geoRegion ?? 'WW';
  const placename = geoPlacename ?? 'Worldwide';
  const googleSiteVerification = process.env.REACT_APP_GSC_VERIFICATION;

  // Default structured data for Organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "At Every Desk",
    "alternateName": "AED",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": `${siteUrl}/assets/Logo.png`,
      "width": 1024,
      "height": 1024
    },
    "image": `${siteUrl}/assets/Logo.png`,
    "description": "Paper Bottle India — leading manufacturer of 100% compostable paper bottles and eco-friendly packaging. CIPET, CPCB & FSSAI certified. At Every Desk (AED) offers compostable bottles, bags, preforms, caps, and food packaging worldwide.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+91-9377004949",
        "contactType": "Customer Service",
        "email": "info@ateverydesk.com",
        "availableLanguage": "English",
        "areaServed": "IN",
        "name": "India Office"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+44-7909494684",
        "contactType": "Customer Service",
        "email": "info@ateverydesk.com",
        "areaServed": "GB",
        "name": "UK / International"
      }
    ],
      "sameAs": [
        "https://www.instagram.com/at_every_desk/",
        "https://www.linkedin.com/company/at-every-desk/"
      ],
    "areaServed": [
      {
        "@type": "Country",
        "name": "India"
      },
      {
        "@type": "Place",
        "name": "Worldwide"
      }
    ]
  };

  // LocalBusiness: add streetAddress, addressLocality, addressRegion, postalCode, openingHours when available
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "At Every Desk",
    "url": siteUrl,
    "telephone": "+91-9377004949",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="At Every Desk" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="en" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="copyright" content="At Every Desk" />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content={region} />
      <meta name="geo.placename" content={placename} />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="coverage" content="worldwide" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Favicon - dedicated small icons for Google Search, browsers, and mobile */}
      <link rel="icon" href={`${siteUrl}/favicon.ico`} sizes="any" />
      <link rel="icon" type="image/png" sizes="48x48" href={`${siteUrl}/favicon-48x48.png`} />
      <link rel="icon" type="image/png" sizes="32x32" href={`${siteUrl}/favicon-32x32.png`} />
      <link rel="icon" type="image/png" sizes="16x16" href={`${siteUrl}/favicon-16x16.png`} />
      <link rel="apple-touch-icon" sizes="180x180" href={`${siteUrl}/apple-touch-icon.png`} />
      <link rel="manifest" href={`${siteUrl}/manifest.json`} />
      <meta name="msapplication-TileImage" content={`${siteUrl}/favicon-48x48.png`} />
      
      {/* Open Graph / Facebook - Complete Set */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:image:secure_url" content={image || defaultImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="At Every Desk" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Twitter Card - Complete Set */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || defaultImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@ateverydesk" />
      <meta name="twitter:creator" content="@ateverydesk" />
      
      {/* Additional Social Media Meta Tags */}
      <meta property="article:author" content="At Every Desk" />
      <meta property="article:publisher" content="https://www.ateverydesk.com" />
      
      {/* Mobile & App Meta Tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="At Every Desk" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=yes" />
      
      {/* Theme & Color */}
      <meta name="theme-color" content="#0b1d16" />
      <meta name="msapplication-TileColor" content="#0b1d16" />
      <meta name="msapplication-navbutton-color" content="#0b1d16" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="At Every Desk" />
      {googleSiteVerification && (
        <meta name="google-site-verification" content={googleSiteVerification} />
      )}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
